import { useState, useCallback, useEffect, useRef } from "react";
import { useAdmin } from "../context/AdminContext";
import { AccountsPanel } from "../panels/AccountsPanel";
import { OrganizationsPanel } from "../panels/OrganizationsPanel";
import { WorkspacesPanel } from "../panels/WorkspacesPanel";
import { TaggerPanel } from "../panels/TaggerPanel";
import { ParticipantPanel } from "../panels/ParticipantPanel";
import { GroupPanel } from "../panels/GroupPanel";
import { ThreadPanel } from "../panels/ThreadPanel";
import { IntegratorPanel } from "../panels/IntegratorPanel";
import { ComposerPanel } from "../panels/ComposerPanel";
import { ReaderPanel } from "../panels/ReaderPanel";
import { IdentifierPanel } from "../panels/IdentifierPanel";
import { MessengerPanel } from "../panels/MessengerPanel";
import { WorkflowPanel } from "../panels/WorkflowPanel";
import { PromptPanel } from "../panels/PromptPanel";
import { ConnectorPanel } from "../panels/ConnectorPanel";
import { CloudProviderPanel } from "../panels/CloudProviderPanel";
import { TracesPanel } from "../panels/TracesPanel";
import { SubscriptionControl } from "../components/SubscriptionControl";
import { ApiKeyGenerator } from "../components/ApiKeyGenerator";
import { SandboxPane, SANDBOX_VISIBLE_KEY, SANDBOX_SIDE_KEY, type Side } from "../components/SandboxPane";
import { Menu } from "lucide-react";

const LAST_TAB_KEY = 'admin-last-tab';

interface WebSocketToast {
  id: string;
  channel: string;
  participantId?: string;
  content: string;
  timestamp: number;
}

type TabId = 'accounts' | 'organizations' | 'workspaces' | 'cloud-provider' | 'tagger' | 'participants' | 'groups' | 'integrator' | 'thread' | 'composer' | 'reader' | 'messenger' | 'workflow' | 'identifier' | 'prompts' | 'connector' | 'traces' | 'receiver' | 'sender' | 'authorizer';

interface Tab {
  id: TabId;
  label: string;
  component: React.ComponentType;
  enabled: boolean;
  isAccountTab?: boolean; // Purple accent for account-related tabs
}

const tabs: Tab[] = [
  { id: 'accounts', label: 'Accounts', component: AccountsPanel, enabled: true, isAccountTab: true },
  { id: 'organizations', label: 'Organizations', component: OrganizationsPanel, enabled: true, isAccountTab: true },
  { id: 'workspaces', label: 'Workspaces', component: WorkspacesPanel, enabled: true, isAccountTab: true },
  { id: 'cloud-provider', label: 'Tools/Hooks', component: CloudProviderPanel, enabled: true, isAccountTab: true },
  { id: 'tagger', label: 'Tags', component: TaggerPanel, enabled: true },
  { id: 'participants', label: 'Participants', component: ParticipantPanel, enabled: true },
  { id: 'groups', label: 'Groups', component: GroupPanel, enabled: true },
  { id: 'integrator', label: 'Integrations', component: IntegratorPanel, enabled: true },
  { id: 'thread', label: 'Threads', component: ThreadPanel, enabled: true },
  { id: 'composer', label: 'Composer', component: ComposerPanel, enabled: true },
  { id: 'reader', label: 'Reader', component: ReaderPanel, enabled: true },
  { id: 'identifier', label: 'Identifier', component: IdentifierPanel, enabled: true },
  { id: 'messenger', label: 'Messenger', component: MessengerPanel, enabled: true },
  // Platform admin (M2M) only until n8n is per account (Q95b, Q98a); human logins never qualify (R44).
  { id: 'workflow', label: 'Workflow', component: WorkflowPanel, enabled: false },
  { id: 'prompts', label: 'Prompts', component: PromptPanel, enabled: true },
  { id: 'connector', label: 'Connectors', component: ConnectorPanel, enabled: true },
  { id: 'traces', label: 'Traces', component: TracesPanel, enabled: true },
  { id: 'receiver', label: 'Receiver', component: () => <div className="p-4">Receiver - Coming Soon</div>, enabled: false },
  { id: 'sender', label: 'Sender', component: () => <div className="p-4">Sender - Coming Soon</div>, enabled: false },
  { id: 'authorizer', label: 'Authorizer', component: () => <div className="p-4">Authorizer - Coming Soon</div>, enabled: false },
];

export function AdminPanel() {
  const { adminApp, authConfig, account, tagger, participant, group, thread, integrator, composer, reader, identifier, messenger, workflow, cloudProvider } = useAdmin();
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const saved = localStorage.getItem(LAST_TAB_KEY) as TabId | null;
    return saved && tabs.find(t => t.id === saved)?.enabled ? saved : 'accounts';
  });
  const [showWorkspaceInput, setShowWorkspaceInput] = useState(false);
  const [workspaceInput, setWorkspaceInput] = useState(adminApp.workspaceId);
  const [showNamespaceInput, setShowNamespaceInput] = useState(false);
  const [namespaceInput, setNamespaceInput] = useState(adminApp.namespace);
  const [refreshingTab, setRefreshingTab] = useState(false);
  const [toasts, setToasts] = useState<Map<string, WebSocketToast>>(new Map());
  const workspaceInputRef = useRef<HTMLDivElement>(null);

  // Sandbox pane state
  const [sandboxVisible, setSandboxVisible] = useState(() => {
    const saved = localStorage.getItem(SANDBOX_VISIBLE_KEY);
    return saved ? saved === 'true' : true; // Default visible
  });

  const [sandboxSide, setSandboxSide] = useState<Side>(() => {
    const saved = localStorage.getItem(SANDBOX_SIDE_KEY) as Side | null;
    return saved || 'left';
  });

  // Persist sandbox visibility
  useEffect(() => {
    localStorage.setItem(SANDBOX_VISIBLE_KEY, sandboxVisible.toString());
  }, [sandboxVisible]);

  // Persist sandbox side
  useEffect(() => {
    localStorage.setItem(SANDBOX_SIDE_KEY, sandboxSide);
  }, [sandboxSide]);

  const toggleSandbox = useCallback(() => {
    setSandboxVisible(prev => !prev);
  }, []);

  // Cancel workspace editing
  const cancelWorkspaceEdit = useCallback(() => {
    setShowWorkspaceInput(false);
    setWorkspaceInput(adminApp.workspaceId);
  }, [adminApp.workspaceId]);

  // Handle escape key, click outside, and window blur for workspace input
  useEffect(() => {
    if (!showWorkspaceInput) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelWorkspaceEdit();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (workspaceInputRef.current && !workspaceInputRef.current.contains(e.target as Node)) {
        cancelWorkspaceEdit();
      }
    };
    const handleBlur = () => cancelWorkspaceEdit();

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('blur', handleBlur);
    };
  }, [showWorkspaceInput, cancelWorkspaceEdit]);

  // Persist active tab
  useEffect(() => {
    localStorage.setItem(LAST_TAB_KEY, activeTab);
  }, [activeTab]);

  // Subscribe to ALL WebSocket messages for active subscriptions and show toasts
  useEffect(() => {
    const timeoutRefs = new Map<string, NodeJS.Timeout>();

    const handleWebSocketMessage = (message: any) => {
      console.log('🍞 [AdminPanel] WebSocket message for toast:', message);

      // Extract channel and event data
      const channel = message.channel || 'unknown';
      const event = message.event || message;

      // Extract participantId and content based on event type
      let participantId: string | undefined;
      let content = '';

      if (event.participantId) {
        participantId = event.participantId;
      }

      // Determine content based on event type
      if (event.type === 'tag') {
        // Tag events: created/updated/deleted
        const action = event.action || 'changed';
        const tagName = event.tag?.name || event.tagId || 'tag';
        content = `Tag ${action}: ${tagName}`;
      } else if (event.type === 'assignment') {
        // Tag assignment events
        const action = event.action || 'changed';
        const tagId = event.assignment?.tagId || 'tag';
        content = `Tag ${action}: ${tagId.substring(0, 8)}...`;
      } else if (event.type === 'trace_event') {
        // Trace events from CloudProviderDO
        content = `[${event.eventType}] ${event.summary || `seq=${event.seq}`}`;
      } else if (event.type === 'composing') {
        // Composing events
        content = event.content?.substring(0, 50) + (event.content?.length > 50 ? '...' : '') || 'composing...';
      } else if (event.type === 'typing') {
        // Typing events
        content = 'typing...';
      } else if (event.content) {
        // Generic message content
        content = event.content.substring(0, 50) + (event.content.length > 50 ? '...' : '');
      } else if (event.timestamp) {
        // Fallback for events with just timestamp
        content = 'event received';
      }

      // Use channel as key for debouncing
      const toastKey = channel;
      const newToast: WebSocketToast = {
        id: toastKey,
        channel,
        participantId,
        content,
        timestamp: Date.now(),
      };

      // Clear existing timeout for this channel
      const existingTimeout = timeoutRefs.get(toastKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      console.log('✅ [AdminPanel] Updating toast for channel:', toastKey);
      setToasts((prev) => {
        const updated = new Map(prev);
        updated.set(toastKey, newToast);
        return updated;
      });

      // Auto-remove after 3 seconds
      const timeout = setTimeout(() => {
        console.log('🗑️ [AdminPanel] Removing toast:', toastKey);
        setToasts((prev) => {
          const updated = new Map(prev);
          updated.delete(toastKey);
          return updated;
        });
        timeoutRefs.delete(toastKey);
      }, 3000);

      timeoutRefs.set(toastKey, timeout);
    };

    // Subscribe to all active channels
    const unsubscribers: Array<() => void> = [];

    adminApp.activeSubscriptions.forEach((channelName) => {
      console.log('🔔 [AdminPanel] Subscribing to channel for toasts:', channelName);
      const unsubscribe = adminApp.subscribeToChannel(channelName, handleWebSocketMessage);
      unsubscribers.push(unsubscribe);
    });

    return () => {
      console.log('🔴 [AdminPanel] Cleaning up toast subscriptions');
      unsubscribers.forEach(unsub => unsub());
      timeoutRefs.forEach(timeout => clearTimeout(timeout));
    };
  }, [JSON.stringify(Array.from(adminApp.activeSubscriptions)), adminApp.subscribeToChannel]);

  // Refresh tab data on switch
  const handleTabSwitch = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
    setRefreshingTab(true);

    // Trigger refresh for the tab's entity
    const refreshPromise = (async () => {
      switch (tabId) {
        case 'accounts':
          await account.accounts.loadEntities();
          break;
        case 'organizations':
          await account.organizations.loadEntities();
          break;
        case 'workspaces':
          await account.workspaces.loadEntities();
          break;
        case 'tagger':
          await tagger.loadEntities();
          break;
        case 'participants':
          await participant.loadEntities();
          break;
        case 'groups':
          await group.loadEntities();
          break;
        case 'thread':
          thread.clearThread();
          await thread.loadThreads(50, 0);
          break;
        case 'integrator':
          await Promise.all([
            integrator.integrations.loadIntegrations(),
            integrator.connections.loadConnections(),
          ]);
          break;
        case 'composer':
          // Composer loads on-demand per participant search
          break;
        case 'reader':
          // Reader loads on-demand per participant/thread query
          reader.clearState();
          break;
        case 'identifier':
          // Identifier loads mappings on-demand
          identifier.clearState();
          break;
        case 'messenger':
          // Messenger loads on-demand
          messenger.clearState();
          break;
        case 'workflow':
          // Workflow loads on-demand
          workflow.proxy.clearState();
          break;
        case 'cloud-provider':
          await cloudProvider.loadAll();
          break;
      }
    })();

    refreshPromise.finally(() => setRefreshingTab(false));
  }, [tagger, participant, group, thread, integrator, composer, reader, identifier, messenger, workflow, cloudProvider]);

  const handleWorkspaceChange = useCallback(() => {
    if (workspaceInput.trim()) {
      adminApp.setWorkspaceId(workspaceInput.trim());
      setShowWorkspaceInput(false);
    }
  }, [workspaceInput, adminApp]);

  const handleNamespaceChange = useCallback(() => {
    if (namespaceInput.trim()) {
      adminApp.setNamespace(namespaceInput.trim());
      setShowNamespaceInput(false);
    }
  }, [namespaceInput, adminApp]);

  const ActiveComponent = tabs.find(t => t.id === activeTab)?.component;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Workspace Selector */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm">
            {/* Burger Menu for Sandbox Toggle */}
            <button
              onClick={toggleSandbox}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title={sandboxVisible ? 'Hide Sandbox' : 'Show Sandbox'}
            >
              <Menu size={18} className="text-gray-600 dark:text-gray-400" />
            </button>

            <div className={`w-2 h-2 rounded-full ${adminApp.isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-gray-600 dark:text-gray-400">
              {adminApp.isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm">
            {/* API Key Generator */}
            <ApiKeyGenerator
              baseUrl={adminApp.httpUrl}
              workspaceId={adminApp.workspaceId}
              authConfig={authConfig}
            />

            {/* Subscription Control */}
            <SubscriptionControl
              onSubscribe={adminApp.subscribe}
              onUnsubscribe={adminApp.unsubscribe}
              activeSubscriptions={adminApp.activeSubscriptions}
            />

            {showWorkspaceInput ? (
              <div ref={workspaceInputRef} className="flex items-center gap-2">
                <label className="text-gray-600 dark:text-gray-400">Workspace:</label>
                <input
                  type="text"
                  value={workspaceInput}
                  onChange={(e) => setWorkspaceInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleWorkspaceChange()}
                  placeholder="Enter workspace ID..."
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-w-[200px]"
                  autoFocus
                />
                <button
                  onClick={handleWorkspaceChange}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  Switch
                </button>
                <button
                  onClick={cancelWorkspaceEdit}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowWorkspaceInput(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Click to switch workspace"
              >
                <span className="text-gray-600 dark:text-gray-400">Workspace:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{adminApp.workspaceId}</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}

            {showNamespaceInput ? (
              <div className="flex items-center gap-2">
                <label className="text-gray-600 dark:text-gray-400">Namespace:</label>
                <input
                  type="text"
                  value={namespaceInput}
                  onChange={(e) => setNamespaceInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNamespaceChange()}
                  placeholder="Enter namespace..."
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-w-[150px]"
                  autoFocus
                />
                <button
                  onClick={handleNamespaceChange}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  Set
                </button>
                <button
                  onClick={() => {
                    setShowNamespaceInput(false);
                    setNamespaceInput(adminApp.namespace);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowNamespaceInput(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Click to change namespace"
              >
                <span className="text-gray-600 dark:text-gray-400">Namespace:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{adminApp.namespace}</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}

            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {adminApp.httpUrl}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="flex space-x-1 px-4" aria-label="Tabs">
          {tabs.map(tab => {
            const isAccountTab = tab.isAccountTab;
            const activeColor = isAccountTab ? 'purple' : 'blue';

            return (
              <button
                key={tab.id}
                onClick={() => tab.enabled && handleTabSwitch(tab.id)}
                disabled={!tab.enabled}
                className={`
                  px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2
                  border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? `border-${activeColor}-500 text-${activeColor}-600 dark:text-${activeColor}-400`
                    : tab.enabled
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      : 'border-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  }
                `}
                style={activeTab === tab.id ? {
                  borderBottomColor: isAccountTab ? '#a855f7' : '#3b82f6',
                  color: isAccountTab ? '#a855f7' : '#3b82f6',
                } : undefined}
              >
                {tab.label}
                {activeTab === tab.id && refreshingTab && (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {!tab.enabled && <span className="ml-1 text-xs">(Soon)</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area with Sandbox */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sandbox Pane (left side) */}
        {sandboxVisible && sandboxSide === 'left' && (
          <SandboxPane
            isVisible={sandboxVisible}
            onVisibilityChange={setSandboxVisible}
            side={sandboxSide}
            onSideChange={setSandboxSide}
          />
        )}

        {/* Tab Content */}
        <div className="flex-1 overflow-auto p-6">
          {ActiveComponent && <ActiveComponent />}
        </div>

        {/* Sandbox Pane (right side) */}
        {sandboxVisible && sandboxSide === 'right' && (
          <SandboxPane
            isVisible={sandboxVisible}
            onVisibilityChange={setSandboxVisible}
            side={sandboxSide}
            onSideChange={setSandboxSide}
          />
        )}
      </div>

      {/* WebSocket Event Toasts */}
      {toasts.size > 0 && (
        <div className="fixed top-20 right-4 z-50 space-y-2 max-w-sm">
          {Array.from(toasts.values()).map((toast) => (
            <div
              key={toast.id}
              className="bg-blue-600 text-white px-4 py-2 rounded shadow-lg flex flex-col gap-1 animate-fade-in"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono opacity-75">{toast.channel}</span>
                {toast.participantId && (
                  <span className="text-xs font-medium">{toast.participantId.substring(0, 8)}...</span>
                )}
              </div>
              {toast.content && (
                <span className="text-xs opacity-90">{toast.content}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
